import { ImageOff } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { money, coverSrc, coverPosition } from "@/lib/api";

const SELECTION_TONE = {
  Considering: "bg-line text-stone",
  Shortlisted: "bg-line text-stone",
  Presented: "bg-[#EDEFC4] text-ink",
  "Awaiting Approval": "bg-[#F6DED6] text-[#A8492E]",
  Approved: "bg-[#BEC221] text-ink",
  "Revision Requested": "bg-[#F6DED6] text-[#A8492E]",
  Declined: "bg-ink text-white",
};

const PROC_TONE = {
  "Not Ready": "bg-line text-stone",
  "Ready to Order": "bg-[#EDEFC4] text-ink",
  Ordered: "bg-[#BEC221] text-ink",
  Acknowledged: "bg-[#BEC221] text-ink",
  Backordered: "bg-[#F6DED6] text-[#A8492E]",
  "In Production": "bg-[#EDEFC4] text-ink",
  "Ready to Ship": "bg-[#EDEFC4] text-ink",
  Shipped: "bg-ink text-white",
  "Partially Delivered": "bg-ink text-white",
  Delivered: "bg-[#DDE3C8] text-ink",
  Damaged: "bg-[#E07A5F] text-white",
  "Return Requested": "bg-[#F6DED6] text-[#A8492E]",
  Returned: "bg-[#E07A5F] text-white",
  Installed: "bg-[#C9CE6B] text-ink",
  Cancelled: "bg-line text-fog line-through",
};

export const Pill = ({ children, tone = "bg-line text-stone", testId }) => (
  <span
    data-testid={testId}
    className={`inline-flex items-center whitespace-nowrap rounded-sm px-2 py-[3px] text-[11px] font-medium tracking-wide ${tone}`}
  >
    {children}
  </span>
);

export const SelectionBadge = ({ status, testId }) =>
  status ? <Pill testId={testId} tone={SELECTION_TONE[status] || "bg-line text-stone"}>{status}</Pill> : null;

export const ProcurementBadge = ({ status, testId }) =>
  status ? <Pill testId={testId} tone={PROC_TONE[status] || "bg-line text-stone"}>{status}</Pill> : null;

const CLIENT_TONE = {
  "Needs Your Approval": "bg-[#F6DED6] text-[#A8492E]",
  "Alternative Requested": "bg-[#F6DED6] text-[#A8492E]",
  Approved: "bg-[#BEC221] text-ink",
  Ordered: "bg-[#DDE3C8] text-ink",
  "Order Processing": "bg-[#EDEFC4] text-ink",
  "Preparing to Ship": "bg-[#EDEFC4] text-ink",
  "On the Way": "bg-ink text-white",
  "Partially Delivered": "bg-ink text-white",
  Delivered: "bg-[#DDE3C8] text-ink",
  Installed: "bg-[#C9CE6B] text-ink",
  "Contact Designer": "bg-[#E07A5F] text-white",
  "Update In Progress": "bg-[#F6DED6] text-[#A8492E]",
  "Selection In Progress": "bg-line text-stone",
};

export const ClientStatusBadge = ({ status, testId }) =>
  status ? <Pill testId={testId} tone={CLIENT_TONE[status] || "bg-line text-stone"}>{status}</Pill> : null;

export const Thumb = ({ src, alt, className = "h-12 w-12", rounded = "rounded-sm" }) =>
  src ? (
    <img
      src={src}
      alt={alt || ""}
      loading="lazy"
      className={`${className} ${rounded} object-cover bg-line`}
    />
  ) : (
    <div className={`${className} ${rounded} flex items-center justify-center bg-[#F1F0EC] border border-line`}>
      <ImageOff className="h-4 w-4 text-fog" strokeWidth={1.5} />
    </div>
  );

/** Project cover art: stored file, legacy URL, or a graceful placeholder if it can't load. */
export const Cover = ({ project, className = "h-full w-full", rounded = "rounded-none", imgClassName = "", testId }) => {
  const [failed, setFailed] = useState(false);
  const src = coverSrc(project);
  if (!src || failed) return <Thumb className={className} rounded={rounded} />;
  return (
    <img
      data-testid={testId}
      src={src}
      alt={project?.name || ""}
      loading="lazy"
      onError={() => setFailed(true)}
      style={{ objectPosition: coverPosition(project) }}
      className={`${className} ${rounded} object-cover bg-line ${imgClassName}`}
    />
  );
};

export const PageHeader = ({ overline, title, subtitle, actions, testId }) => (  <div data-testid={testId} className="flex flex-col gap-5 border-b border-line pb-8 sm:flex-row sm:items-end sm:justify-between">
    <div>
      {overline && <div className="overline mb-3">{overline}</div>}
      <h1 className="font-serif text-3xl tracking-tight text-ink sm:text-4xl">{title}</h1>
      {subtitle && <p className="mt-3 max-w-2xl text-sm text-stone leading-relaxed">{subtitle}</p>}
    </div>
    {actions && <div className="flex flex-wrap items-center gap-3">{actions}</div>}
  </div>
);

export const MetricCard = ({ label, value, hint, testId, accent, to }) => {
  const inner = (
    <>
      <div className="overline">{label}</div>
      <div className={`num mt-4 text-3xl font-medium ${accent ? "text-lime-dark" : "text-ink"}`}>{value}</div>
      {hint && <div className="mt-2 text-xs text-fog">{hint}</div>}
      {to && (
        <div className="mt-4 text-[10px] uppercase tracking-[0.15em] text-fog transition-colors group-hover:text-lime-dark">
          View →
        </div>
      )}
    </>
  );
  return to ? (
    <Link data-testid={testId} to={to} className="card-surface card-hover group block p-6 transition-colors hover:border-lime">
      {inner}
    </Link>
  ) : (
    <div data-testid={testId} className="card-surface card-hover p-6">{inner}</div>
  );
};

export const MoneyStat = ({ label, value, sub, testId, tone = "text-ink" }) => (
  <div data-testid={testId} className="border-l-2 border-line pl-4">
    <div className="overline">{label}</div>
    <div className={`num mt-2 text-xl ${tone}`}>{money(value)}</div>
    {sub && <div className="mt-1 text-xs text-fog">{sub}</div>}
  </div>
);

export const EmptyState = ({ title, body, action, testId }) => (
  <div data-testid={testId} className="card-surface flex flex-col items-center justify-center gap-3 px-6 py-16 text-center">
    <h3 className="font-serif text-xl text-ink">{title}</h3>
    {body && <p className="max-w-md text-sm text-stone">{body}</p>}
    {action}
  </div>
);

export const Field = ({ label, children, hint, className = "" }) => (
  <label className={`block ${className}`}>
    <span className="overline block mb-2">{label}</span>
    {children}
    {hint && <span className="mt-1 block text-[11px] text-fog">{hint}</span>}
  </label>
);

export const SectionTitle = ({ children, action }) => (
  <div className="mb-6 flex items-end justify-between gap-4">
    <h2 className="font-serif text-2xl tracking-tight text-ink">{children}</h2>
    {action}
  </div>
);

export const Progress = ({ value = 0 }) => (
  <div className="h-[3px] w-full bg-line">
    <div className="h-full bg-lime transition-[width] duration-500" style={{ width: `${Math.min(100, value)}%` }} />
  </div>
);
